/**
 * Button — componente genérico de botão.
 *
 * variant   — primary | secondary | outline | ghost
 * size      — sm | md | lg  (quando não informado, padding via className como antes)
 * isLoading — exibe spinner (Loader2) e desabilita interação
 * ...props  — repassa todos os atributos nativos de <button> (incluindo onClick, type, aria-*)
 *
 * Backward compat: uso com className="px-5 py-2.5" continua funcionando — quando
 * `size` não é fornecido, nenhuma classe de padding/radius é adicionada pelo Button.
 */

import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Loader2 } from "lucide-react";

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export type ButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "line";
export type ButtonSize    = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:   ButtonVariant;
  /** Quando informado, aplica padding e border-radius pré-definidos. */
  size?:      ButtonSize;
  isLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Mapeamentos
// ---------------------------------------------------------------------------

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    "bg-linear-to-r from-primary to-secondary text-background " +
    "shadow-lg shadow-secondary/20 hover:shadow-xl hover:shadow-secondary/30",
  secondary:
    "border border-secondary/30 bg-secondary/10 text-primary backdrop-blur-sm " +
    "hover:border-secondary/50 hover:bg-secondary/20",
  outline:
    "border border-primary/30 text-primary bg-transparent hover:border-primary/60",
  ghost:
    "text-text-main hover:text-secondary",
  /**
   * Secundário do REDESIGN — o irmão do `primary` na mesma tela.
   *
   * `outline` é pré-redesign (`border-primary/30` + `text-primary`) e não podia
   * ser corrigido no lugar: ele ainda veste `PrivateLeagueHero` e
   * `ImageCropModal`, que são outras superfícies. Esta variante existe porque o
   * padrão já estava escrito à mão em pelo menos seis lugares
   * (`AuthForm`, `BookDetailTabs`, `RereadConfirmModal`, `BookDetailHero`…),
   * sempre com os mesmos tokens — e cada cópia dessas é um `<button>` cru que o
   * lint de DS-drift acusa com razão.
   */
  line:
    "border-[1.5px] border-line bg-surface text-ink hover:border-accent/50",
};

/** Aplicados SOMENTE quando size é fornecido explicitamente */
const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: "gap-1.5 rounded-xl   px-4 py-2    text-xs",
  md: "gap-2   rounded-2xl  px-5 py-3    text-sm",
  lg: "gap-2   rounded-2xl  px-6 py-4    text-lg",
};

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant   = "primary",
      size,
      isLoading = false,
      disabled,
      children,
      className = "",
      ...props
    },
    ref
  ) {
    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={[
          "inline-flex cursor-pointer items-center justify-center",
          "font-body font-semibold transition-all duration-200",
          "disabled:cursor-not-allowed disabled:opacity-70",
          // rounded-full somente quando size não é fornecido (backward compat)
          !size && "rounded-full",
          "active:scale-95",
          VARIANT_CLASSES[variant],
          size ? SIZE_CLASSES[size] : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        {...props}
      >
        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";
